import classNames from 'classnames';
import type { ComponentProps, FC, ReactNode } from 'react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Props as MaskProps } from 'react-input-mask';
import InputMask from 'react-input-mask';
import PreFilledByAITooltip from '../PreFilledByAITooltip.component';

type Ref<T> =
  | React.RefObject<T | null>
  | ((ref: T | null) => void)
  | undefined
  | null;

type OriginalInputProps = Omit<
  JSX.IntrinsicElements['input'],
  'size' | 'ref' | 'type'
> & {
  ref?: Ref<HTMLInputElement>;
};

// type OriginalTextAreaProps = Omit<
//   JSX.IntrinsicElements['textarea'],
//   'size' | 'ref' | 'type'
// > & {
//   ref?: Ref<HTMLTextAreaElement>;
// };

type BaseInputRenderProps = {
  onFocus: () => void;
  onBlur: () => void;
  className: string;
};

export type BaseInputProps = OriginalInputProps & {
  type?: JSX.IntrinsicElements['input']['type'] | 'textarea';
  size?: 'xl' | 'base' | 'md' | 'sm' | 'xs';
  Icon?: FC<ComponentProps<'svg'>> | false;
  LeftIcon?: FC<ComponentProps<'svg'>> | false;
  RightIcon?: FC<ComponentProps<'svg'>> | false;
  renderExtraLeft?: (className: string) => ReactNode;
  renderExtraRight?: (className: string) => ReactNode;
  error?: string | boolean;
  removeLeftRounding?: boolean;
  removeRightRounding?: boolean;
  containerClassName?: string;
  containerRef?: Ref<HTMLDivElement>;
  renderInput?: (renderProps: BaseInputRenderProps) => ReactNode;
  iconClassName?: string;
  innerClassName?: string;
  aiTooltip?: {
    enabled?: boolean;
    description?: string | null;
  };
} & Partial<
    Pick<
      MaskProps,
      'mask' | 'alwaysShowMask' | 'maskPlaceholder' | 'beforeMaskedStateChange'
    >
  >;
// 1.5 + 1.25 + 1 = 3.75
const outerPaddingClasses = {
  xl: { l: 'pl-[1.25rem]', r: 'pr-[1.25rem]', t: 'pt-4.5', b: 'pb-4.5' },
  // large: { l: 'pl-[1rem]', r: 'pr-[1rem]', t: 'pt-4', b: 'pb-4' },
  base: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-3', b: 'pb-3' }, // 'px-2.5 py-3',
  md: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-2.5', b: 'pb-2.5' }, //'p-2.5'
  sm: { l: 'pl-[0.5rem]', r: 'pr-[0.5rem]', t: 'pt-2', b: 'pb-2' }, //'p-2',
  xs: { l: 'pl-[0.375rem]', r: 'pr-[0.375rem]', t: 'pt-1.5', b: 'pb-1.5' }, // 'p-1.5',
};

// left padding + icon width + icon right padding
const leftPaddingForInputWithIcon = {
  xl: 'pl-[3.75rem]',
  base: 'pl-[2.625rem]',
  md: 'pl-[2.625rem]',
  sm: 'pl-[2.5rem]',
  xs: 'pl-[2.25rem]',
};

const rightPaddingForInputWithIcon = {
  xl: 'pr-[3.75rem]',
  base: 'pr-[2.625rem]',
  md: 'pr-[2.625rem]',
  sm: 'pr-[2.5rem]',
  xs: 'pr-[2.25rem]',
};

// left indent for icons is same as left padding for input
const leftIndentForIcons = {
  xl: 'left-[1.25rem]',
  base: 'left-[0.625rem]',
  md: 'left-[0.625rem]',
  sm: 'left-[0.5rem]',
  xs: 'left-[0.375rem]',
};

const rightIndentForIcons = {
  xl: 'right-[1.25rem]',
  base: 'right-[0.625rem]',
  md: 'right-[0.625rem]',
  sm: 'right-[0.5rem]',
  xs: 'right-[0.375rem]',
};

type State = {
  // isHovered: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  hasValue: boolean;
  hasError: boolean;
  size: BaseInputProps['size'];
};

type ContainerOptions = {
  defaultInnerClassName?: string;
  useAIColorScheme?: boolean;
};
const getContainerStateClasses = (
  state: State,
  { defaultInnerClassName, useAIColorScheme }: ContainerOptions,
) => {
  const { isFocused, isDisabled, hasValue, hasError } = state;

  const defaultShadow = 'shadow-[0px_1px_2px_0px_rgba(53,26,77,0.03)]';

  if (isDisabled) {
    return classNames(
      'bg-primary-50 cursor-not-allowed border',
      hasError ? 'border-[#EF4444]' : 'border-primary-100',
    );
  }

  if (hasError) {
    return classNames(
      'relative z-[3] border border-[#Ef4444] bg-white',
      isFocused ? 'ring-2 ring-primary-100' : 'hover:bg-primary-50',
      useAIColorScheme ? '' : 'transition',
    );
  }

  if (isFocused) {
    return classNames(
      'relative z-[3] bg-white border border-primary ring-2 ring-primary-100',
      useAIColorScheme ? '' : 'transition',
    );
  }

  return classNames(
    defaultShadow,
    useAIColorScheme
      ? 'ai-input-bg-border'
      : 'bg-white transition hover:bg-primary-50 border border-primary-100',
  );
};

const getInputStateClasses = (state: State) => {
  const { isFocused, isDisabled, hasValue, hasError, size } = state;

  const baseTextColor = 'text-primary';

  const generalClasses = classNames(
    'flex-1 min-w-0 font-assistant !outline-none !border-0 transition bg-transparent !shadow-none',
    size === 'xl' ? 'text-lg font-semibold' : 'text-base leading-[1.5rem]',
  );

  if (isDisabled) {
    return classNames(generalClasses, 'placeholder-primary-400 text-primary');
  }

  if (isFocused) {
    return classNames(generalClasses, 'placeholder-primary-400', baseTextColor);
  }

  return classNames(generalClasses, 'placeholder-primary-400', baseTextColor);
};

const getIconStateClasses = (state: State) => {
  const { isFocused, isDisabled, hasValue, hasError } = state;

  if (isDisabled) {
    return 'text-primary-200';
  }

  if (isFocused) {
    return 'text-primary';
  }

  if (hasValue) {
    return 'text-primary';
  }

  return 'text-primary-400';
};

const BaseInput: FC<BaseInputProps> = forwardRef((props, ref) => {
  const {
    size = 'base',
    Icon,
    LeftIcon,
    RightIcon,
    removeLeftRounding,
    removeRightRounding,
    containerClassName,
    containerRef,
    renderExtraLeft,
    renderExtraRight,
    renderInput,
    iconClassName,
    innerClassName,
    aiTooltip,
    ...inputProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const hasValue = !!inputProps.value;

  const inputRef = useRef<HTMLInputElement>(null);

  const LeftIconComponent = LeftIcon || Icon;
  const RightIconComponent = RightIcon;

  const state = {
    isFocused,
    isDisabled: !!inputProps.disabled,
    hasValue,
    hasError: !!inputProps.error,
    size,
  };

  const inputPaddingClasses = useMemo(() => {
    let map = outerPaddingClasses[size];
    if (LeftIconComponent) {
      map = { ...map, l: leftPaddingForInputWithIcon[size] };
    }
    if (RightIconComponent) {
      map = { ...map, r: rightPaddingForInputWithIcon[size] };
    }
    return Object.values(map).join(' ');
  }, [size, !!LeftIconComponent, !!RightIconComponent]);

  const inputStateClasses = getInputStateClasses(state);

  const containerStateClasses = getContainerStateClasses(state, {
    defaultInnerClassName: innerClassName,
    useAIColorScheme: aiTooltip?.enabled,
  });

  const innerContainerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(containerRef, () => innerContainerRef.current);

  const roundingClasses = {
    'rounded-l-[0.5rem]': !removeLeftRounding,
    'rounded-r-[0.5rem]': !removeRightRounding,
  };

  const commonInputProps: typeof inputProps = {
    ...inputProps,
    ref,
    className: classNames(
      props.className,
      inputPaddingClasses,
      inputStateClasses,
      roundingClasses,
    ),
    onFocus: (e) => {
      setIsFocused(true);
      inputProps.onFocus?.(e);
    },
    onBlur: (e) => {
      setIsFocused(false);
      inputProps.onBlur?.(e);
    },
  };

  useImperativeHandle(commonInputProps.ref, () => inputRef.current);

  const extraContentPaddingClasses = Object.values(
    outerPaddingClasses[size],
  ).join(' ');

  return (
    <PreFilledByAITooltip
      enabled={!state.isDisabled && aiTooltip?.enabled}
      hide={isFocused}
      description={aiTooltip?.description}
    >
      <div
        ref={innerContainerRef}
        className={classNames(
          'flex min-w-0',
          containerClassName,
          roundingClasses,
          containerStateClasses,
        )}
      >
        {renderExtraLeft?.(extraContentPaddingClasses)}
        <div className={classNames('relative flex flex-1 min-w-0')}>
          {!!LeftIconComponent && (
            <div
              className={classNames(
                'h-[1.5rem] w-[1.5rem] overflow-hidden pointer-events-none absolute top-1/2 -translate-y-1/2',
                leftIndentForIcons[size],
              )}
            >
              <span
                className={classNames('abs-center', getIconStateClasses(state))}
              >
                <LeftIconComponent
                  className={classNames(
                    iconClassName,
                    'h-[1.5rem] w-[1.5rem] transition-colors',
                  )}
                />
              </span>
            </div>
          )}
          {(() => {
            if (renderInput) {
              return renderInput({
                className: commonInputProps.className,
                onFocus: commonInputProps.onFocus,
                onBlur: commonInputProps.onBlur,
              } as BaseInputRenderProps);
            }
            if (commonInputProps.mask) {
              return (
                <InputMask
                  {...commonInputProps}
                  mask={commonInputProps.mask}
                  ref={undefined}
                  inputRef={inputRef}
                />
              );
            }
            if (commonInputProps.type === 'textarea') {
              return (
                <textarea
                  {...(commonInputProps as any)}
                  ref={inputRef as any}
                />
              );
            }
            return <input {...commonInputProps} ref={inputRef} />;
          })()}
          {!!RightIconComponent && (
            <div
              className={classNames(
                'h-[1.5rem] w-[1.5rem] overflow-hidden pointer-events-none absolute top-1/2 -translate-y-1/2',
                rightIndentForIcons[size],
              )}
            >
              <span
                className={classNames('abs-center', getIconStateClasses(state))}
              >
                <RightIconComponent
                  className={classNames(
                    iconClassName,
                    'h-[1.5rem] w-[1.5rem] transition-colors',
                  )}
                />
              </span>
            </div>
          )}
        </div>
        {renderExtraRight?.(extraContentPaddingClasses)}
      </div>
    </PreFilledByAITooltip>
  );
});

BaseInput.displayName = 'BaseInput';
export default BaseInput;
